// Local (non-global-store) filter state alerts' create/edit form drives
// FilterControl with. Alerts has no zustand panel to key updates into (unlike
// charts-v2) and no v1-shaped persistence to round-trip through (unlike a
// dashboard widget tile — see `widget-tile-filter-bridge.ts`), so this holds
// the v2 shape directly: exactly the three fields `FilterControl` needs
// (`selectedFilterOptions`/`smartSearchValue`/`keywordFilters`) map onto.
import type {
  AlertBody,
  AlertType,
  FilterStateV2,
} from 'features/alerts/service/types'
import type {
  NumericOperatorType,
  OperatorType,
  SelectedFilterOption,
} from 'features/charts-v2/types'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
// bully ignore[no-cross-feature-private-imports: translateV1FiltersToPanel is the established v1<->v2 filter-translation seam already used by dashboards-v2-1's build-dashboard-options-panel.ts (which this file's synthetic-panel pattern mirrors) and use-embeddable-chart-v2-filters.ts. permanent: alerts has no v1-shaped store of its own to route this through instead — see this file's module doc comment.]
import { translateV1FiltersToPanel } from 'features/charts-v2/utils/translate-v1-filters-to-panel'
import type { Filters } from 'types/project'

export type AlertFilterState = {
  filters: SelectedFilterOption[]
  // Alerts has no smart-search affordance (legacy alerts never had one — see
  // AlertDefinition's doc comment) — this stays `null` (hidden) at every call
  // site, but is threaded as real state so `FilterControl`'s required prop
  // contract is satisfied without a bag of no-ops scattered across callers.
  smartSearchValue: string | null | undefined
  keywordFilters: Record<string, string[] | null>
}

export const emptyAlertFilterState: AlertFilterState = {
  filters: [],
  smartSearchValue: null,
  keywordFilters: {},
}

/**
 * Add/update/delete a field's selection in a `SelectedFilterOption[]` list.
 * Mirrors the charts-v2 store's `updateFilterSelection` reducer and
 * `widget-tile-filter-bridge.ts`'s `tryUpdateFilterSelection` — same shape,
 * inlined locally because alerts has no v1-representability constraint to
 * enforce (it writes `filters_v2` directly) and no immer-produce draft to
 * mutate (this is a plain immutable array).
 */
export function applyFilterSelectionUpdate(
  filters: readonly SelectedFilterOption[],
  field: string,
  operator: OperatorType | NumericOperatorType,
  values: string[],
  range?: {
    min?: number
    max?: number
    valueType?: number
    numericValue?: number
  },
): SelectedFilterOption[] {
  const existingIndex = filters.findIndex((f) => f.field === field)

  const isDeleteOperation =
    values.length === 0 &&
    range?.numericValue === undefined &&
    range?.min === undefined &&
    range?.max === undefined &&
    range?.valueType === undefined

  if (isDeleteOperation) {
    if (existingIndex < 0) return [...filters]
    return filters.filter((f) => f.field !== field)
  }

  const newFilter: SelectedFilterOption = {
    field,
    operator,
    values,
    ...(range ?? {}),
  } as SelectedFilterOption

  if (existingIndex >= 0) {
    const next = [...filters]
    next[existingIndex] = newFilter
    return next
  }
  return [...filters, newFilter]
}

/** Drops `null` (removed) entries — the wire/`FilterStateV2` shape only ever
 * carries present operators, unlike `FilterControl`'s `keywordFilters` prop
 * shape which uses `null` to mean "no card for this operator". */
export function cleanKeywordFilters(
  keywordFilters: Record<string, string[] | null>,
): Record<string, string[]> {
  const cleaned: Record<string, string[]> = {}
  for (const [operator, values] of Object.entries(keywordFilters)) {
    if (values !== null) cleaned[operator] = values
  }
  return cleaned
}

/** Seed state when editing an existing alert — `alert.filters_v2` is always
 * populated by the backend (computed from legacy columns as a fallback for
 * pre-migration rows), so no legacy fallback is needed here. */
export function alertFilterStateFromFiltersV2(
  filtersV2: FilterStateV2,
): AlertFilterState {
  return {
    filters: filtersV2.filters,
    smartSearchValue: null,
    keywordFilters: filtersV2.searchCriteria,
  }
}

// Placeholder fields `translateV1FiltersToPanel` never reads for this
// from-scratch seed (it only reads `existingPanel.filters`/`.timeBucket` to
// preserve what v1 can't express — both empty/null here, since there is no
// prior panel). Mirrors `buildDashboardOptionsPanel`'s synthetic-panel
// pattern (dashboards-v2-1/utils/build-dashboard-options-panel.ts), which
// solves the exact same "bridge one v1-shaped input into v2 filter shape"
// problem for a dashboard widget tile.
const SYNTHETIC_PANEL_BASE: ChartPanel = {
  id: 'alert-request-synthetic',
  title: '',
  isCustomTitle: false,
  order: 0,
  widthMode: 'full',
  chartType: ChartTypeV2.BAR_HORIZONTAL,
  metrics: [],
  breakdowns: [],
  filters: [],
  dateRange: { startDate: 0, endDate: 0 },
  timeBucket: null,
  searchTerms: [],
  searchCriteria: {},
  presetId: null,
  canvasMode: 'chart',
  sortDirection: 'DESC',
  chartFontSize: 'M',
  showXAxisLabel: true,
  showYAxisLabel: true,
  showDatapointLabels: false,
  xAxisOverflow: 'expand',
  yAxisOverflow: 'expand',
  showSentimentZones: true,
  showRatingZones: true,
  scaleVolume: false,
  showTrendline: false,
  breakdownLabelMode: 'tooltip',
  showLegend: true,
  showBreakdownHierarchy: false,
  showCardLabels: false,
  tableFontSize: 'M',
  pageSize: 'all',
  currentPage: 1,
}

/** Seed state for a brand-new alert prefilled from a chart ("create alert
 * from this data point") — `CreateAlertProps.alertRequest` is still legacy
 * `Filters`, produced upstream by charts-v2/dashboards' click handlers (out
 * of this migration's scope). Reuses the established v1<->v2 translation
 * seam rather than re-deriving the same keyword/search precedence rules. */
export function alertFilterStateFromLegacyRequest(
  filters: Filters,
): AlertFilterState {
  const updates = translateV1FiltersToPanel(filters, SYNTHETIC_PANEL_BASE)
  return {
    filters: updates.filters ?? [],
    smartSearchValue: null,
    keywordFilters: updates.searchCriteria ?? {},
  }
}

/** Builds the `filters_v2` request payload from local state — the write path
 * for both create and update once the two migrated components own the
 * state. `dateRange` is always null: alerts has no date-range picker (see
 * `AlertFilterState`'s doc comment on `smartSearchValue` for the analogous
 * "never surfaced by this UI" reasoning), so there is never an explicit date
 * filter to report. */
export function buildFiltersV2Payload(state: AlertFilterState): FilterStateV2 {
  return {
    filters: state.filters,
    searchTerms: [],
    searchCriteria: cleanKeywordFilters(state.keywordFilters),
    dateRange: null,
  }
}

/**
 * Builds the alert create/update request body. Extracted out of
 * `create-alert/Container.tsx`'s `onSubmit` so the "never leak a stale
 * legacy filter snapshot" invariant is independently unit-testable, not
 * only reachable by mounting the whole form.
 *
 * `formValues` (react-hook-form's submitted `AlertType`) is seeded from the
 * pre-edit `alert` when editing — its `filters`/`search_terms`/
 * `search_criteria`/`search_terms_logical` are stale copies of whatever the
 * alert had BEFORE this edit, not bound to any input the user can change
 * through the v2 UI. Explicitly nulling them here (after the `...formValues`
 * spread) is what stops that stale snapshot from riding along next to the
 * user's actual edit in `filters_v2` — an update's `{...alert, ...alertPayload}`
 * merge (see `Container.tsx`) only omits a key from the final PUT body when
 * `alertPayload` itself already carries `undefined` for it, since a later
 * key always wins in an object spread regardless of its value.
 *
 * `reviews_filter` is deliberately left untouched: it is a separate,
 * still-live "minimum record count" threshold with no `filters_v2`
 * equivalent, not part of this migration.
 */
export function buildAlertPayload(
  formValues: AlertType,
  filterState: AlertFilterState,
  recipients: string[],
  status: string,
): AlertBody {
  return {
    ...formValues,
    filters_v2: buildFiltersV2Payload(filterState),
    filters: undefined,
    search_terms: undefined,
    search_criteria: undefined,
    search_terms_logical: undefined,
    shared_users: recipients,
    status,
  }
}
